import React, { useState, useEffect } from 'react';
import { AiOutlineCreditCard, AiOutlineQrcode, AiOutlineCheck, AiOutlineClose, AiOutlineLoading3Quarters, AiOutlineUser, AiOutlineLock, AiOutlineDownload, AiOutlineUpload, AiOutlineBank } from 'react-icons/ai';

type PaymentType = 'credit' | 'pix' | 'crypto-in' | 'crypto-out' | 'fiat-out' | null;

const CheckoutPage: React.FC<{
  clientName?: string;
  clientLogo?: string;
  fixedAmount?: number;
  duration?: number;
}> = ({
  clientName = "Checkout Solutions",
  clientLogo = "https://via.placeholder.com/50/6366f1/ffffff?text=TF",
  fixedAmount,
  duration = 900
}) => {
  const [step, setStep] = useState<1 | 2>(1);
  const [paymentType, setPaymentType] = useState<PaymentType>(null);
  const [amount, setAmount] = useState<string>(fixedAmount?.toString() || '');
  const [timeLeft, setTimeLeft] = useState(duration);
  const [paymentStatus, setPaymentStatus] = useState<'idle' | 'processing' | 'success' | 'failed'>('idle');
  const [linkStatus, setLinkStatus] = useState<'running' | 'expired'>('running');
  const [isUsed, setIsUsed] = useState<'yes' | 'no'>('no');
  const [initialLoading, setInitialLoading] = useState(true);
  const [formData, setFormData] = useState({
    cardNumber: '', cardName: '', expiry: '', cvv: '', email: '', walletAddress: '', pixKey: ''
  });

  useEffect(() => {
    if ( timeLeft > 0) {
      setLinkStatus("running")
      const timer = setInterval(() => setTimeLeft(prev => prev - 1), 1000);
      return () => clearInterval(timer);
    } else {
      setLinkStatus("expired")
    }
  }, [step, timeLeft]);

  const formatTime = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
  };

  const paymentOptions = [
    { id: 'credit', label: 'One Time Payment', sublabel: 'Credit or debit card', icon: AiOutlineCreditCard },
    { id: 'pix', label: 'PIX', sublabel: 'Instant Brazilian payment', icon: AiOutlineQrcode },
    { id: 'crypto-in', label: 'Crypto Payin', sublabel: 'BTC, ETH, USDT', icon: AiOutlineDownload },
    { id: 'crypto-out', label: 'Crypto Payout', sublabel: 'Receive in crypto', icon: AiOutlineUpload },
    { id: 'fiat-out', label: 'Fiat Payout', sublabel: 'Bank transfer', icon: AiOutlineBank }
  ];

  const handlePaymentSelect = (type: PaymentType) => {
    setPaymentType(type);
    setTimeout(() => setStep(2), 300);
  };

  const handleSubmit = () => {
    setPaymentStatus('processing');
    setTimeout(() => setPaymentStatus(Math.random() > 0.3 ? 'success' : 'failed'), 2000);
  };

  const resetCheckout = () => {
    setStep(1);
    setPaymentType(null);
    setPaymentStatus('idle');
    setTimeLeft(duration);
    setAmount(fixedAmount?.toString() || '');
    setFormData({ cardNumber: '', cardName: '', expiry: '', cvv: '', email: '', walletAddress: '', pixKey: '' });
  };

  useEffect(() => {
    setInitialLoading(false)
  }, []);

  if (initialLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-indigo-900 to-purple-900 flex items-center justify-center p-4 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-teal-500/30 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/30 rounded-full blur-3xl animate-pulse" style={{animationDelay:'1s'}} />
          <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl animate-pulse" style={{animationDelay:'2s'}} />
        </div>
        <div className="text-center relative z-10">
          <div className="relative w-32 h-32 mx-auto mb-6">
            <AiOutlineLoading3Quarters className="text-7xl text-teal-400 animate-spin mx-auto" />
            <div className="absolute inset-0 bg-teal-400/20 rounded-full blur-2xl animate-pulse" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2 animate-fadeIn">Preparing Checkout...</h2>
          <p className="text-gray-400 animate-fadeIn" style={{animationDelay:'0.2s'}}>Please wait while we load your payment details</p>
        </div>
      </div>
    );
  }

  if (isUsed === 'yes') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-indigo-900 to-purple-900 flex items-center justify-center p-4 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-orange-500/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-yellow-500/20 rounded-full blur-3xl animate-pulse" style={{animationDelay:'1s'}} />
        </div>
        <div className="relative bg-gray-900/80 backdrop-blur-2xl rounded-3xl p-12 max-w-md w-full shadow-2xl border border-orange-500/30 animate-slideUp">
          <div className="text-center">
            <div className="mx-auto w-28 h-28 rounded-full flex items-center justify-center mb-8 bg-orange-500/30 animate-scaleIn">
              <div className="w-20 h-20 rounded-full flex items-center justify-center bg-orange-500 animate-pulse">
                <AiOutlineCheck className="text-5xl text-white" />
              </div>
            </div>
            <h2 className="text-4xl font-bold mb-4 text-orange-400 animate-fadeIn" style={{animationDelay:'0.2s'}}>
              Link Already Used!
            </h2>
            <p className="text-gray-400 mb-4 text-lg animate-fadeIn" style={{animationDelay:'0.4s'}}>
              This payment link has already been used for a transaction.
            </p>
            <p className="text-gray-500 text-sm animate-fadeIn" style={{animationDelay:'0.6s'}}>
              Each payment link can only be used once. Please request a new payment link from the merchant.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (linkStatus === 'expired' && paymentStatus === 'idle') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-indigo-900 to-purple-900 flex items-center justify-center p-4 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-red-500/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-orange-500/20 rounded-full blur-3xl animate-pulse" style={{animationDelay:'1s'}} />
        </div>
        <div className="relative bg-gray-900/80 backdrop-blur-2xl rounded-3xl p-12 max-w-md w-full shadow-2xl border border-red-500/30 animate-slideUp">
          <div className="text-center">
            <div className="mx-auto w-28 h-28 rounded-full flex items-center justify-center mb-8 bg-red-500/30 animate-scaleIn">
              <div className="w-20 h-20 rounded-full flex items-center justify-center bg-red-500 animate-pulse">
                <AiOutlineClose className="text-5xl text-white" />
              </div>
            </div>
            <h2 className="text-4xl font-bold mb-4 text-red-400 animate-fadeIn" style={{animationDelay:'0.2s'}}>
              Link Expired!
            </h2>
            <p className="text-gray-400 mb-4 text-lg animate-fadeIn" style={{animationDelay:'0.4s'}}>
              This payment link has expired and is no longer valid.
            </p>
            <p className="text-gray-500 text-sm animate-fadeIn" style={{animationDelay:'0.6s'}}>
              Please contact the merchant to receive a new payment link.
            </p>
          </div>
        </div>
      </div>
    );
  }
  
  if (paymentStatus === 'success' || paymentStatus === 'failed') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-indigo-900 to-purple-900 flex items-center justify-center p-4 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-teal-500/30 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/30 rounded-full blur-3xl animate-pulse" style={{animationDelay:'1s'}} />
        </div>

        <div className="relative bg-gray-900/60 backdrop-blur-2xl rounded-3xl p-12 max-w-md w-full shadow-2xl border border-white/10 animate-slideUp">
          {/* <button onClick={resetCheckout} className="absolute top-6 right-6 text-gray-400 hover:text-white transition-all hover:rotate-90 duration-300">
            <AiOutlineClose className="text-xl" />
          </button> */}
          
          <div className="text-center">
            <div className={`mx-auto w-28 h-28 rounded-full flex items-center justify-center mb-8 ${
              paymentStatus === 'success' ? 'bg-teal-500/30' : 'bg-red-500/30'
            } animate-scaleIn`}>
              <div className={`w-20 h-20 rounded-full flex items-center justify-center ${
                paymentStatus === 'success' ? 'bg-teal-500 animate-ping-once' : 'bg-red-500 animate-shake'
              }`}>
                {paymentStatus === 'success' ? (
                  <AiOutlineCheck className="text-5xl text-white animate-checkmark" />
                ) : (
                  <AiOutlineClose className="text-5xl text-white" />
                )}
              </div>
            </div>
            
            <h2 className={`text-4xl font-bold mb-4 animate-fadeIn ${
              paymentStatus === 'success' ? 'text-teal-400' : 'text-red-400'
            }`} style={{animationDelay:'0.2s'}}>
              {paymentStatus === 'success' ? 'Payment Successful!' : 'Payment Failed'}
            </h2>
            
            <p className="text-gray-400 mb-8 text-lg animate-fadeIn" style={{animationDelay:'0.4s'}}>
              {paymentStatus === 'success' ? 'Your transaction has been completed successfully.' : 'Something went wrong with your payment.'}
            </p>
            
            {/* <button onClick={resetCheckout} className="w-full bg-gradient-to-r from-teal-500 via-teal-400 to-blue-500 text-white py-4 rounded-2xl font-bold text-lg hover:shadow-2xl hover:shadow-teal-500/50 transition-all duration-300 hover:scale-105 animate-fadeIn" style={{animationDelay:'0.6s'}}>
              Done
            </button> */}
          </div>
        </div>
      </div>
    );
  }

  if (paymentStatus === 'processing') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-indigo-900 to-purple-900 flex items-center justify-center p-4 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-teal-500/30 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/30 rounded-full blur-3xl animate-pulse" style={{animationDelay:'1s'}} />
        </div>
        <div className="text-center relative z-10">
          <div className="relative w-40 h-40 mx-auto mb-8">
            <div className="absolute inset-0 border-4 border-transparent border-t-teal-400 border-r-teal-400 rounded-full animate-spin" />
            <div className="absolute inset-3 border-4 border-transparent border-b-purple-400 border-l-purple-400 rounded-full animate-spin-reverse" />
            <div className="absolute inset-6 bg-gradient-to-br from-teal-400 to-purple-500 rounded-full animate-pulse flex items-center justify-center">
              <AiOutlineCreditCard className="text-4xl text-white animate-bounce-slow" />
            </div>
            <div className="absolute inset-0 bg-teal-400/20 rounded-full blur-2xl animate-pulse" />
            <div className="absolute top-0 left-1/2 w-3 h-3 bg-teal-400 rounded-full animate-orbit" style={{transformOrigin: '0 80px'}} />
            <div className="absolute top-0 left-1/2 w-3 h-3 bg-purple-400 rounded-full animate-orbit" style={{transformOrigin: '0 80px', animationDelay: '0.5s'}} />
          </div>
          <h2 className="text-3xl font-bold text-white mb-3 animate-fadeIn">Processing Payment...</h2>
          <p className="text-gray-400 text-lg mb-4 animate-fadeIn" style={{animationDelay:'0.2s'}}>Please wait while we securely process your transaction</p>
          <div className="flex items-center justify-center gap-2 animate-fadeIn" style={{animationDelay:'0.4s'}}>
            <div className="w-2 h-2 bg-teal-400 rounded-full animate-pulse" />
            <div className="w-2 h-2 bg-teal-400 rounded-full animate-pulse" style={{animationDelay:'0.2s'}} />
            <div className="w-2 h-2 bg-teal-400 rounded-full animate-pulse" style={{animationDelay:'0.4s'}} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-indigo-900 to-purple-900 py-12 px-4 relative overflow-hidden">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-teal-500/20 rounded-full blur-3xl animate-float" style={{animationDelay:'2s'}} />
        <div className="absolute top-1/2 right-0 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl animate-float" style={{animationDelay:'4s'}} />
      </div>

      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:50px_50px]" />

      <div className="max-w-2xl mx-auto relative z-10">
        <div className="flex items-center justify-between mb-8 animate-slideDown">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center shadow-lg shadow-purple-500/50 animate-pulse">
              <AiOutlineLock className="text-2xl text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white">Secure Checkout</h1>
          </div>
          {step === 2 && (
            <div className="flex items-center gap-2 text-gray-300 text-sm bg-white/5 px-4 py-2 rounded-full backdrop-blur-xl border border-white/10">
                {/* <AiOutlineLock className="text-base" />
                <span>SSL Encrypted</span> */}
                <button onClick={() => setStep(1)} className="text-gray-300 hover:text-white flex items-center gap-2 transition-all duration-300 text-sm hover:translate-x-[-4px] group">
                <span className="group-hover:animate-bounceLeft">←</span> Back
                </button>
            </div>
          )}
        </div>

        {/* <div className="flex items-center justify-center gap-3 mb-10 animate-slideDown" style={{animationDelay:'0.1s'}}>
          <div className="flex items-center gap-2 transition-all duration-500">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm transition-all duration-500 ${
              step === 1 ? 'bg-white text-indigo-900 shadow-lg shadow-white/50' : 'bg-teal-500 text-white shadow-lg shadow-teal-500/50'
            }`}>
              {step === 2 ? <AiOutlineCheck className="text-lg" /> : '1'}
            </div>
            <span className={`text-sm font-medium transition-all duration-500 ${step === 1 ? 'text-white' : 'text-gray-400'}`}>Select Method</span>
          </div>
          
          <div className={`w-16 h-0.5 transition-all duration-500 ${step === 2 ? 'bg-gradient-to-r from-teal-500 to-teal-400' : 'bg-gray-600'}`} />
          
          <div className="flex items-center gap-2 transition-all duration-500">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm transition-all duration-500 ${
              step === 2 ? 'bg-white text-indigo-900 shadow-lg shadow-white/50' : 'bg-gray-600 text-gray-400'
            }`}>2</div>
            <span className={`text-sm font-medium transition-all duration-500 ${step === 2 ? 'text-white' : 'text-gray-400'}`}>Complete Payment</span>
          </div>
        </div> */}

        {step === 1 && (
          <div className="space-y-5 animate-slideUp">
            <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-white/20 transition-all duration-300 hover:shadow-xl hover:shadow-teal-500/10 group">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs text-gray-400 uppercase tracking-wider mb-1.5">Merchant</div>
                  <div className="text-white font-bold text-xl group-hover:text-teal-400 transition-colors">{clientName}</div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-teal-400 animate-pulse" />
                  <div className="text-white font-bold text-2xl">{formatTime(timeLeft)}</div>
                </div>
              </div>
            </div>

            {!fixedAmount && (
              <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-white/20 transition-all duration-300 hover:shadow-xl hover:shadow-teal-500/10">
                <div className="text-xs text-gray-400 uppercase tracking-wider mb-4">Enter Amount</div>
                <div className="bg-white rounded-xl p-5 flex items-center justify-between hover:shadow-lg transition-all duration-300 hover:scale-[1.02]">
                  <div className="flex items-baseline gap-2 flex-1">
                    <span className="text-gray-400 text-2xl font-medium">$</span>
                    <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="bg-transparent text-4xl font-bold text-gray-400 outline-none w-full" placeholder="0.00" step="0.01" />
                  </div>
                  <span className="text-gray-400 text-lg font-medium">USD</span>
                </div>
              </div>
            )}

            <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-white/20 transition-all duration-300">
              <div className="text-xs text-gray-400 uppercase tracking-wider mb-4">Payment Method</div>
              <div className="space-y-3">
                {paymentOptions.map((opt, i) => (
                  <button disabled={!amount} key={opt.id} onClick={() => handlePaymentSelect(opt.id as PaymentType)} className={`w-full bg-white/5 hover:bg-white/10 border border-white/10 hover:border-teal-500/50 rounded-xl p-4 flex items-center justify-between group transition-all duration-300 hover:scale-[1.02] hover:shadow-lg hover:shadow-teal-500/20 animate-fadeIn ${
                      paymentType === opt.id ? 'border-teal-500 bg-white/10 shadow-lg shadow-teal-500/30' : ''
                    }`} style={{animationDelay:`${i*0.1}s`}}>
                    <div className="flex items-center gap-4">
                      <div className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center text-gray-300 group-hover:text-teal-400 transition-all duration-300 group-hover:scale-110 group-hover:rotate-6">
                        <opt.icon className="text-2xl" />
                      </div>
                      <div className="text-left">
                        <div className="text-white font-semibold text-base group-hover:text-teal-400 transition-colors">{opt.label}</div>
                        <div className="text-gray-400 text-sm">{opt.sublabel}</div>
                      </div>
                    </div>
                    <div className={`w-5 h-5 rounded-full border-2 transition-all duration-300 ${
                      paymentType === opt.id ? 'border-teal-500 bg-teal-500 scale-110' : 'border-gray-500 group-hover:border-teal-400'
                    }`}>
                      {paymentType === opt.id && <div className="w-full h-full flex items-center justify-center"><div className="w-2 h-2 rounded-full bg-white animate-ping-once" /></div>}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-center gap-2 text-gray-400 text-sm pt-4 animate-fadeIn" style={{animationDelay:'0.5s'}}>
              <AiOutlineLock className="text-base animate-pulse" />
              <span>256-bit encryption · PCI DSS compliant</span>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-5 animate-slideUp">
            {/* <button onClick={() => setStep(1)} className="text-gray-300 hover:text-white flex items-center gap-2 transition-all duration-300 text-sm hover:translate-x-[-4px] group">
              <span className="group-hover:animate-bounceLeft">←</span> Back
            </button> */}

            <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-white/20 transition-all duration-300">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs text-gray-400 uppercase tracking-wider mb-1.5">Merchant</div>
                  <div className="text-white font-bold text-xl">{clientName}</div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-teal-400 animate-pulse" />
                  <div className={`text-white font-bold text-2xl ${timeLeft < 60 ? 'text-red-400 animate-pulse' : ''}`}>{formatTime(timeLeft)}</div>
                </div>
              </div>
            </div>

            <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-teal-500/30 transition-all duration-300 hover:shadow-xl hover:shadow-teal-500/20 group">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs text-gray-400 uppercase tracking-wider mb-2">Total Amount</div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-gray-400 text-2xl">$</span>
                    <span className="text-teal-400 text-5xl font-bold group-hover:scale-105 transition-transform">{amount || '0.00'}</span>
                    <span className="text-gray-400 text-lg">USD</span>
                  </div>
                </div>
                <div className="w-12 h-12 rounded-xl bg-teal-500/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <AiOutlineLock className="text-2xl text-teal-400 group-hover:animate-pulse" />
                </div>
              </div>
            </div>

            <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-white/20 transition-all duration-300">
              {/* <div className="text-xs text-gray-400 uppercase tracking-wider mb-6">
                {paymentType === 'credit' ? 'One Time Payment' : paymentType === 'pix' ? 'PIX Payment' : paymentType === 'crypto-in' ? 'Crypto Payin' : paymentType === 'crypto-out' ? 'Crypto Payout' : 'Fiat Payout'}
              </div> */}

              <div className="space-y-5">
                {paymentType === 'credit' && (
                    <>
                        <div className="animate-fadeIn"><label className="block text-gray-400 text-sm mb-2.5">Card Number</label>
                            <div className="relative">
                                <AiOutlineCreditCard className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-xl" />
                                <input type="text" value={formData.cardNumber} onChange={(e) => setFormData({...formData, cardNumber: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-xl px-12 py-4 text-white placeholder-gray-500 focus:border-teal-500/50 focus:outline-none transition-all duration-300 focus:scale-[1.02] focus:shadow-lg focus:shadow-teal-500/20" placeholder="1234 5678 9012 3456" />
                            </div>
                        </div>
                        <div className="animate-fadeIn" style={{animationDelay:'0.1s'}}><label className="block text-gray-400 text-sm mb-2.5">Cardholder Name</label>
                            <div className="relative">
                                <AiOutlineUser className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-xl" />
                                <input type="text" value={formData.cardName} onChange={(e) => setFormData({...formData, cardName: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-xl px-12 py-4 text-white placeholder-gray-500 focus:border-teal-500/50 focus:outline-none transition-all duration-300 focus:scale-[1.02] focus:shadow-lg focus:shadow-teal-500/20" placeholder="John Doe" />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="animate-fadeIn" style={{animationDelay:'0.2s'}}><label className="block text-gray-400 text-sm mb-2.5">Expiry Date</label>
                                <div className="relative">
                                <AiOutlineCreditCard className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-xl" />
                                <input type="text" value={formData.expiry} onChange={(e) => setFormData({...formData, expiry: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-xl px-12 py-4 text-white placeholder-gray-500 focus:border-teal-500/50 focus:outline-none transition-all duration-300 focus:scale-[1.02] focus:shadow-lg focus:shadow-teal-500/20" placeholder="MM/YY" />
                                </div>
                            </div>
                            <div className="animate-fadeIn" style={{animationDelay:'0.3s'}}><label className="block text-gray-400 text-sm mb-2.5">CVV</label>
                                <div className="relative">
                                <AiOutlineLock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-xl" />
                                <input type="password" value={formData.cvv} onChange={(e) => setFormData({...formData, cvv: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-xl px-12 py-4 text-white placeholder-gray-500 focus:border-teal-500/50 focus:outline-none transition-all duration-300 focus:scale-[1.02] focus:shadow-lg focus:shadow-teal-500/20" placeholder="•••" />
                                </div>
                            </div>
                        </div>
                    </>
                )}

                {paymentType === 'pix' && (
                    <>
                        <div className="animate-fadeIn"><label className="block text-gray-400 text-sm mb-2.5">Card Number</label>
                            <div className="relative">
                                <AiOutlineCreditCard className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-xl" />
                                <input type="text" value={formData.cardNumber} onChange={(e) => setFormData({...formData, cardNumber: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-xl px-12 py-4 text-white placeholder-gray-500 focus:border-teal-500/50 focus:outline-none transition-all duration-300 focus:scale-[1.02] focus:shadow-lg focus:shadow-teal-500/20" placeholder="1234 5678 9012 3456" />
                            </div>
                        </div>
                        <div className="animate-fadeIn" style={{animationDelay:'0.1s'}}><label className="block text-gray-400 text-sm mb-2.5">Cardholder Name</label>
                            <div className="relative">
                                <AiOutlineUser className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-xl" />
                                <input type="text" value={formData.cardName} onChange={(e) => setFormData({...formData, cardName: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-xl px-12 py-4 text-white placeholder-gray-500 focus:border-teal-500/50 focus:outline-none transition-all duration-300 focus:scale-[1.02] focus:shadow-lg focus:shadow-teal-500/20" placeholder="John Doe" />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="animate-fadeIn" style={{animationDelay:'0.2s'}}><label className="block text-gray-400 text-sm mb-2.5">Expiry Date</label>
                                <div className="relative">
                                <AiOutlineCreditCard className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-xl" />
                                <input type="text" value={formData.expiry} onChange={(e) => setFormData({...formData, expiry: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-xl px-12 py-4 text-white placeholder-gray-500 focus:border-teal-500/50 focus:outline-none transition-all duration-300 focus:scale-[1.02] focus:shadow-lg focus:shadow-teal-500/20" placeholder="MM/YY" />
                                </div>
                            </div>
                            <div className="animate-fadeIn" style={{animationDelay:'0.3s'}}><label className="block text-gray-400 text-sm mb-2.5">CVV</label>
                                <div className="relative">
                                <AiOutlineLock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-xl" />
                                <input type="password" value={formData.cvv} onChange={(e) => setFormData({...formData, cvv: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-xl px-12 py-4 text-white placeholder-gray-500 focus:border-teal-500/50 focus:outline-none transition-all duration-300 focus:scale-[1.02] focus:shadow-lg focus:shadow-teal-500/20" placeholder="•••" />
                                </div>
                            </div>
                        </div>
                    </>
                    // <div className="animate-fadeIn">
                    //     <label className="block text-gray-400 text-sm mb-2.5">PIX Key</label>
                    //     <input type="text" value={formData.pixKey} onChange={(e) => setFormData({...formData, pixKey: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-4 text-white placeholder-gray-500 focus:border-teal-500/50 focus:outline-none transition-all duration-300 focus:scale-[1.02] focus:shadow-lg focus:shadow-teal-500/20" placeholder="Enter your PIX key" />
                    // </div>
                )}

                {(paymentType === 'crypto-in' || paymentType === 'crypto-out') && (
                    <>
                        <div className="animate-fadeIn"><label className="block text-gray-400 text-sm mb-2.5">Card Number</label>
                            <div className="relative">
                                <AiOutlineCreditCard className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-xl" />
                                <input type="text" value={formData.cardNumber} onChange={(e) => setFormData({...formData, cardNumber: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-xl px-12 py-4 text-white placeholder-gray-500 focus:border-teal-500/50 focus:outline-none transition-all duration-300 focus:scale-[1.02] focus:shadow-lg focus:shadow-teal-500/20" placeholder="1234 5678 9012 3456" />
                            </div>
                        </div>
                        <div className="animate-fadeIn" style={{animationDelay:'0.1s'}}><label className="block text-gray-400 text-sm mb-2.5">Cardholder Name</label>
                            <div className="relative">
                                <AiOutlineUser className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-xl" />
                                <input type="text" value={formData.cardName} onChange={(e) => setFormData({...formData, cardName: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-xl px-12 py-4 text-white placeholder-gray-500 focus:border-teal-500/50 focus:outline-none transition-all duration-300 focus:scale-[1.02] focus:shadow-lg focus:shadow-teal-500/20" placeholder="John Doe" />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="animate-fadeIn" style={{animationDelay:'0.2s'}}><label className="block text-gray-400 text-sm mb-2.5">Expiry Date</label>
                                <div className="relative">
                                <AiOutlineCreditCard className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-xl" />
                                <input type="text" value={formData.expiry} onChange={(e) => setFormData({...formData, expiry: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-xl px-12 py-4 text-white placeholder-gray-500 focus:border-teal-500/50 focus:outline-none transition-all duration-300 focus:scale-[1.02] focus:shadow-lg focus:shadow-teal-500/20" placeholder="MM/YY" />
                                </div>
                            </div>
                            <div className="animate-fadeIn" style={{animationDelay:'0.3s'}}><label className="block text-gray-400 text-sm mb-2.5">CVV</label>
                                <div className="relative">
                                <AiOutlineLock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-xl" />
                                <input type="password" value={formData.cvv} onChange={(e) => setFormData({...formData, cvv: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-xl px-12 py-4 text-white placeholder-gray-500 focus:border-teal-500/50 focus:outline-none transition-all duration-300 focus:scale-[1.02] focus:shadow-lg focus:shadow-teal-500/20" placeholder="•••" />
                                </div>
                            </div>
                        </div>
                    </>
                    // <div className="animate-fadeIn">
                    //     <label className="block text-gray-400 text-sm mb-2.5">Wallet Address</label>
                    //     <input type="text" value={formData.walletAddress} onChange={(e) => setFormData({...formData, walletAddress: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-4 text-white placeholder-gray-500 focus:border-teal-500/50 focus:outline-none transition-all duration-300 focus:scale-[1.02] focus:shadow-lg focus:shadow-teal-500/20 font-mono text-sm" placeholder="0x..." />
                    // </div>
                )}

                {paymentType === 'fiat-out' && (
                    <>
                        <div className="animate-fadeIn"><label className="block text-gray-400 text-sm mb-2.5">Card Number</label>
                            <div className="relative">
                                <AiOutlineCreditCard className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-xl" />
                                <input type="text" value={formData.cardNumber} onChange={(e) => setFormData({...formData, cardNumber: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-xl px-12 py-4 text-white placeholder-gray-500 focus:border-teal-500/50 focus:outline-none transition-all duration-300 focus:scale-[1.02] focus:shadow-lg focus:shadow-teal-500/20" placeholder="1234 5678 9012 3456" />
                            </div>
                        </div>
                        <div className="animate-fadeIn" style={{animationDelay:'0.1s'}}><label className="block text-gray-400 text-sm mb-2.5">Cardholder Name</label>
                            <div className="relative">
                                <AiOutlineUser className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-xl" />
                                <input type="text" value={formData.cardName} onChange={(e) => setFormData({...formData, cardName: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-xl px-12 py-4 text-white placeholder-gray-500 focus:border-teal-500/50 focus:outline-none transition-all duration-300 focus:scale-[1.02] focus:shadow-lg focus:shadow-teal-500/20" placeholder="John Doe" />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="animate-fadeIn" style={{animationDelay:'0.2s'}}><label className="block text-gray-400 text-sm mb-2.5">Expiry Date</label>
                                <div className="relative">
                                <AiOutlineCreditCard className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-xl" />
                                <input type="text" value={formData.expiry} onChange={(e) => setFormData({...formData, expiry: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-xl px-12 py-4 text-white placeholder-gray-500 focus:border-teal-500/50 focus:outline-none transition-all duration-300 focus:scale-[1.02] focus:shadow-lg focus:shadow-teal-500/20" placeholder="MM/YY" />
                                </div>
                            </div>
                            <div className="animate-fadeIn" style={{animationDelay:'0.3s'}}><label className="block text-gray-400 text-sm mb-2.5">CVV</label>
                                <div className="relative">
                                <AiOutlineLock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-xl" />
                                <input type="password" value={formData.cvv} onChange={(e) => setFormData({...formData, cvv: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-xl px-12 py-4 text-white placeholder-gray-500 focus:border-teal-500/50 focus:outline-none transition-all duration-300 focus:scale-[1.02] focus:shadow-lg focus:shadow-teal-500/20" placeholder="•••" />
                                </div>
                            </div>
                        </div>
                    </>
                    // <div className="animate-fadeIn">
                    //     <label className="block text-gray-400 text-sm mb-2.5">Email Address</label>
                    //     <input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-4 text-white placeholder-gray-500 focus:border-teal-500/50 focus:outline-none transition-all duration-300 focus:scale-[1.02] focus:shadow-lg focus:shadow-teal-500/20" placeholder="your@email.com" />
                    // </div>
                )}
              </div>
            </div>

            <button onClick={handleSubmit} disabled={!amount || parseFloat(amount) <= 0} className="w-full bg-gradient-to-r from-teal-500 via-teal-400 to-blue-500 text-white py-4 rounded-2xl font-bold text-lg hover:shadow-2xl hover:shadow-teal-500/50 transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-none disabled:hover:scale-100 animate-fadeIn relative overflow-hidden group" style={{animationDelay:'0.4s'}}>
              <span className="relative z-10">Pay Now</span>
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
            </button>

            <div className="flex items-center justify-center gap-2 text-gray-400 text-sm pt-4 animate-fadeIn" style={{animationDelay:'0.5s'}}>
              <AiOutlineLock className="text-base animate-pulse" />
              <span>256-bit encryption · PCI DSS compliant</span>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes slideDown { from { opacity: 0; transform: translateY(-20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes scaleIn { from { opacity: 0; transform: scale(0.8); } to { opacity: 1; transform: scale(1); } }
        @keyframes float { 0%, 100% { transform: translate(0, 0); } 50% { transform: translate(30px, -30px); } }
        @keyframes shake { 0%, 100% { transform: translateX(0); } 25% { transform: translateX(-10px); } 75% { transform: translateX(10px); } }
        @keyframes checkmark { from { transform: scale(0) rotate(-45deg); } to { transform: scale(1) rotate(0); } }
        @keyframes ping-once { 75%, 100% { transform: scale(1.2); opacity: 0; } }
        @keyframes bounceLeft { 0%, 100% { transform: translateX(0); } 50% { transform: translateX(-5px); } }
        @keyframes spin-reverse { from { transform: rotate(360deg); } to { transform: rotate(0deg); } }
        @keyframes bounce-slow { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
        @keyframes orbit { from { transform: rotate(0deg) translateX(80px) rotate(0deg); } to { transform: rotate(360deg) translateX(80px) rotate(-360deg); } }
        .animate-slideUp { animation: slideUp 0.5s ease-out; }
        .animate-slideDown { animation: slideDown 0.6s ease-out; }
        .animate-scaleIn { animation: scaleIn 0.5s ease-out; }
        .animate-fadeIn { animation: fadeIn 0.5s ease-out; }
        .animate-float { animation: float 6s ease-in-out infinite; }
        .animate-shake { animation: shake 0.5s ease-in-out; }
        .animate-checkmark { animation: checkmark 0.5s ease-out; }
        .animate-ping-once { animation: ping-once 1s cubic-bezier(0, 0, 0.2, 1); }
        .animate-bounceLeft { animation: bounceLeft 0.5s ease-in-out infinite; }
        .animate-spin-reverse { animation: spin-reverse 2s linear infinite; }
        .animate-bounce-slow { animation: bounce-slow 2s ease-in-out infinite; }
        .animate-orbit { animation: orbit 3s linear infinite; }
        input[type="number"]::-webkit-inner-spin-button, input[type="number"]::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
        input[type="number"] { -moz-appearance: textfield; }
      `}</style>
    </div>
  );
};

export default CheckoutPage;